import { Component, Watch, Mixins } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import {
    cdnService,
    deviceChange,
    FeatureEnabledV2,
    getFeatureEnabled,
    isBreakpointLargeSync,
    isBreakpointMobileSync,
    languageQuery,
} from '@gig/magic-common';
import { NavigationView } from '@gig/magic-sdk/dist/adapter/configs/getNavigation';
import { MagicConfigMixin } from '@gig/magic-common';
import { config, cookie } from '../../services';
import {
    getters,
    actions,
    NAVIGATION_NAMESPACE,
} from '../../store/modules/navigation';
import type { INormalizedNavItem } from '../../store/modules/navigation';
import type { OmitFirst } from '../../types';
import { RouteName } from '../../routes/routes.constants';
import { ACCOUNT_NAMESPACE } from '../../store/modules/account';
import { saveSubMenuVisible, getSubMenuVisible } from './main-navigation.utils';
import { Feature, getRoutePathForFeature, isHybridPnP } from '../../util';
import { magic, NotLocalized } from '@gig/magic-sdk';
import MainNavigationItem from './main-navigation-item.vue';
import {
    REWARD_COUNT,
    MAIN_NAV_CLICKED,
    MESSAGING_LOADED,
    MESSAGING_UNREAD_LIST,
    USER_LOGGED_IN_UPDATED,
} from '@gig/magic-messages';
import { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import { IMessage } from '@gig/magic-sdk/dist/adapter/messages/message';
import { IPromotion } from '@gig/magic-sdk/dist/adapter/promotions/promotion';

const navigationModule = namespace(NAVIGATION_NAMESPACE);
const accountModule = namespace(ACCOUNT_NAMESPACE);

/**
 * The main navigation component renders a list of
 * navigation items retrieved from the backend.
 */
@Component({
    components: {
        'main-navigation-item': MainNavigationItem,
    },
})
export default class MainNavigation extends Mixins(MagicConfigMixin) {
    @FeatureEnabledV2({ slug: Feature.LOYALTY_PROGRESS_BAR })
    isLoyaltyProgressEnabled: boolean = false;

    /**
     * is pure pay and play enabled
     */
    @FeatureEnabledV2({
        slug: Feature.PURE_PAY_AND_PLAY,
    })
    isPurePayAndPlayEnabled = false;

    /**
     * is top navigation enabled
     */
    @FeatureEnabledV2({
        slug: Feature.TOP_NAVIGATION,
    })
    isTopNavigationEnabled = true;

    features: any[] = [];
    RouteName = RouteName;
    isAccountNotLinked = false;
    promotions: Array<IPromotion<NotLocalized>> = [];
    sideBarPromotionCookie: string | undefined = '';
    /**
     * Number of unread messages
     */
    messages = 0;

    /**
     * Number of 'unread' rewards
     */
    rewards: number = 0;

    /**
     * Promotion cookie set
     */
    hidePromotionButtonClicked = false;

    isLoggedIn = false;

    @accountModule.Getter('hasCompletedBankLinking')
    hasCompletedBankLinking!: boolean;

    /**
     * The menu item to be displayed
     */
    @navigationModule.Getter('mainMenu')
    mainMenu!: ReturnType<typeof getters.mainMenu>;

    /**
     * Whether the main navigation bar is open or closed.
     * @type {ReturnType<typeof getters.isMainMenuOpen>}
     */
    @navigationModule.Getter('isMainMenuOpen')
    isMenuOpen!: ReturnType<typeof getters.isMainMenuOpen>;

    /**
     * Emits an action to toggle the menu open/close.
     * @type {OmitFirst<typeof actions.toggleMainMenu>}
     */
    @navigationModule.Action('toggleMainMenu')
    toggleMenu!: OmitFirst<typeof actions.toggleMainMenu>;

    isSubMenuVisible: boolean = getSubMenuVisible();

    /**
     * Emits an action to fetch the main menu from the
     * API.
     */
    @navigationModule.Action('fetchMainMenu')
    private fetchMainMenu!: OmitFirst<typeof actions.fetchMainMenu>;

    private messageSubscription: ISubscription | undefined;
    private rewardCountSubscription: ISubscription | undefined;
    private deviceSubscription: ISubscription | undefined;
    private loggedInSubscription: ISubscription | undefined;

    isMobile: boolean = false;
    isLarge: boolean = false;

    get userMenu() {
        const menuItems = this.mainMenu.map((item) => {
            return this.getItemWithBadge(item);
        });

        const filteredMenu = menuItems.filter((menuItem) =>
            this.features.every((feature: Feature) => {
                const path = getRoutePathForFeature(feature);
                return path ? !menuItem.link.includes(path) : true;
            }),
        );

        if (this.isLoggedIn) {
            return filteredMenu;
        }

        return filteredMenu.filter((item) => !item.authenticated);
    }

    get desktopMainMenu() {
        return this.userMenu.filter((item) => !item.submenu);
    }

    get desktopSubMenu() {
        return this.userMenu.filter((item) => item.submenu);
    }

    get balanceDelay() {
        const isGameplay = this.$route.name === RouteName.GAMEPLAY;
        return isGameplay ? config.gamePlay.balanceDelay : 0;
    }

    get hasLoyaltyProgress() {
        return !!this.isLoggedIn && this.isLoyaltyProgressEnabled;
    }

    get showBankIdLinking() {
        return this.isAccountNotLinked && this.isLoggedIn;
    }

    get signupTranslation() {
        return this.isPurePayAndPlayEnabled
            ? this.$t('main-navigation.pure-pnp.sign-up')
            : this.$t('main-navigation.sign-up');
    }

    get loginTranslation() {
        return this.isPurePayAndPlayEnabled
            ? this.$t('main-navigation.pure-pnp.log-in')
            : this.$t('main-navigation.login');
    }

    get promotion() {
        return this.promotions[0];
    }

    get promotionImageURL() {
        let backgroundURL;

        if (this.isMobile && this.promotion.mobileBackground) {
            backgroundURL = this.promotion.mobileBackground;
        } else {
            backgroundURL = this.promotion.background;
        }

        return cdnService.getPromotionBackgroundUrl(backgroundURL);
    }

    /**
     * Once the component has been created,
     * fetches the main menu items depending
     * on device and locale.
     */

    async created() {
        this.deviceSubscription = deviceChange(() => {
            this.isMobile = isBreakpointMobileSync();
            this.isLarge = isBreakpointLargeSync();
            this.getMainMenu();
        });

        this.messageSubscription = window.eventBus.subscribe<boolean>(
            MESSAGING_LOADED,
            async (event) => {
                if (!event.payload) {
                    this.messages = 0;
                    return;
                }

                this.messages =
                    (
                        await window.queryBus.query<
                            Array<IMessage<NotLocalized>>
                        >(MESSAGING_UNREAD_LIST)
                    )?.length ?? 0;
            },
            { replay: true },
        );

        this.rewardCountSubscription = window.eventBus.subscribe<number>(
            REWARD_COUNT,
            (e) => {
                this.rewards = e.payload ?? 0;
            },
            { replay: true },
        );

        this.loggedInSubscription = window.eventBus.subscribe<boolean>(
            USER_LOGGED_IN_UPDATED,
            (e) => {
                this.isLoggedIn = e.payload ?? false;
                this.checkAccountLinked();
            },
            { replay: true },
        );

        const features = await this.getFeatures();
        if (features.length) {
            this.features = features;
        }

        const promotions = await magic.methods.promotions.filter(
            'side menu',
            this.magicConfig.language,
            this.magicConfig.mobile,
        );

        if (promotions.length) {
            this.promotions = promotions;
        }

        this.getsideBarPromotionCookie();
    }

    destroyed() {
        this.messageSubscription?.unsubscribe();
        this.rewardCountSubscription?.unsubscribe();
        this.deviceSubscription?.unsubscribe();
        this.loggedInSubscription?.unsubscribe();
    }

    async getFeatures() {
        const isPromotionsEnabled = await getFeatureEnabled(Feature.PROMOTIONS);
        const isShopEnabled = await getFeatureEnabled(Feature.SHOP);
        const isRewardsEnabled = await getFeatureEnabled(Feature.REWARDS);
        const isTournamentsEnabled = await getFeatureEnabled(
            Feature.TOURNAMENTS,
        );
        const isDashboardEnabled = await getFeatureEnabled(Feature.DASHBOARD);
        const isSportsbookEnabled = await getFeatureEnabled(Feature.SPORTSBOOK);
        const isBingoLobbyEnabled = await getFeatureEnabled(
            Feature.BINGO_LOBBY,
        );

        const features = [
            !isPromotionsEnabled && Feature.PROMOTIONS,
            !isShopEnabled && Feature.SHOP,
            !isRewardsEnabled && Feature.REWARDS,
            !isTournamentsEnabled && Feature.TOURNAMENTS,
            !isDashboardEnabled && Feature.DASHBOARD,
            !isSportsbookEnabled && Feature.SPORTSBOOK,
            !isBingoLobbyEnabled && Feature.BINGO_LOBBY,
        ].filter((value) => value);

        return features;
    }

    async checkAccountLinked() {
        if (!this.isLoggedIn) {
            return;
        }

        if (!(await isHybridPnP())) {
            return;
        }

        const profileV2Promise: ReturnType<
            typeof magic.methods.users.getProfileV2
        > = magic.invokeCached(60, magic.methods.users, 'getProfileV2')();

        const profile = await profileV2Promise;
        this.isAccountNotLinked = profile.personId === null;
    }

    getItemWithBadge(item: INormalizedNavItem) {
        // TODO: Handle badges differently once new navigation is in place.
        if (item.icon === 'email') {
            return { ...item, badge: this.messages };
        }
        if (item.icon === 'rewards') {
            return { ...item, badge: this.rewards };
        }

        return item;
    }

    onCompetitionInfo() {
        this.$router.push({
            name: 'progress-info',
        });
        this.toggleMenu();
    }

    @Watch('hasCompletedBankLinking')
    onLoggedInChange() {
        this.checkAccountLinked();
    }

    @Watch('magicConfig.language')
    async getMainMenu() {
        this.fetchMainMenu({
            navigationView: this.isMobile
                ? NavigationView.MOBILE
                : NavigationView.DESKTOP,
            language: await languageQuery(),
        });
    }

    onSubMenuToggle(isVisible: boolean) {
        this.isSubMenuVisible = isVisible;
        saveSubMenuVisible(isVisible);
    }

    handleNavigationClick(e: Event) {
        window.eventBus.publish(MAIN_NAV_CLICKED, e);
    }

    getItemTestId(item: Partial<INormalizedNavItem>) {
        return item.label?.toLowerCase();
    }

    goToCTALink() {
        let link;

        if (this.isMobile && this.promotion.values.cta.length) {
            link = this.promotion.values.cta[0].mobile;
        } else {
            link = this.promotion.values.cta[0].desktop;
        }

        return (window.location.href =
            link || this.promotion.values.cta[0].link);
    }

    getsideBarPromotionCookie() {
        this.sideBarPromotionCookie = cookie.read('sideBarPromotionCookie');
    }

    async handlePromotionEyeButton() {
        const date = new Date();
        // set expiration to 24 hours from current time converted to milliseconds
        date.setTime(date.getTime() + 86400000);
        cookie.write('sideBarPromotionCookie', 'sideBarPromotion', {
            expires: date,
        });
        this.getsideBarPromotionCookie();
    }
}
